import { PASSWORD_ERROR_CODES, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '@nexus/core';
import { FormField, Input } from '@nexus/ui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';

/** Text for a failed step of the password login, the password pages or the profile (ADR-0041). */
export function usePasswordError(): (error: unknown) => string {
  const { t } = useTranslation();
  return useCallback(
    (error) => {
      if (!(error instanceof ApiError)) return t('password.errors.unknown');
      if ((PASSWORD_ERROR_CODES as readonly string[]).includes(error.message)) {
        return t(`password.errors.${error.message}`);
      }
      return t(error.status === 429 ? 'password.errors.rate-limited' : 'password.errors.unknown');
    },
    [t],
  );
}

/** Problem with a new password and its repetition, `undefined` when both are fine. */
export function newPasswordProblem(password: string, repeat: string) {
  if (password.length < PASSWORD_MIN_LENGTH) return 'tooShort' as const;
  if (password.length > PASSWORD_MAX_LENGTH) return 'tooLong' as const;
  if (password !== repeat) return 'mismatch' as const;
  return undefined;
}

/**
 * New password with repetition. Length is the only rule (NIST SP 800-63B); the server checks it
 * again.
 */
export function NewPasswordFields({
  password,
  repeat,
  onPassword,
  onRepeat,
  touched,
}: {
  password: string;
  repeat: string;
  onPassword: (value: string) => void;
  onRepeat: (value: string) => void;
  touched: boolean;
}) {
  const { t } = useTranslation();
  const problem = touched ? newPasswordProblem(password, repeat) : undefined;
  return (
    <>
      <FormField
        label={t('password.new')}
        description={t('password.rules', { min: PASSWORD_MIN_LENGTH })}
        required
        error={
          problem === 'tooShort' || problem === 'tooLong'
            ? t(`password.${problem}`, { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH })
            : undefined
        }
      >
        {(field) => (
          <Input
            {...field}
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => onPassword(event.target.value)}
          />
        )}
      </FormField>
      <FormField
        label={t('password.repeat')}
        required
        error={problem === 'mismatch' ? t('password.mismatch') : undefined}
      >
        {(field) => (
          <Input
            {...field}
            type="password"
            autoComplete="new-password"
            value={repeat}
            onChange={(event) => onRepeat(event.target.value)}
          />
        )}
      </FormField>
    </>
  );
}
